import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Carries a migration from the Version Management tab to the Migration and
 * Review pages. The selection lives in memory only: a page reached with an empty
 * slice, or one started for another plugin, sends the user back to the tab.
 */

export type MigrationMode = 'migrate' | 'review';

export interface VersionMigrationState {
  pluginId: string | null;
  merchantPluginIds: string[];
  targetPluginVersionId: string | null;
  /** Generated when the confirmation opens; reused on retry so a replay is idempotent. */
  batchId: string | null;
  /** Succeeded merchants whose custom mappings still need review. */
  flaggedIds: string[];
  mode: MigrationMode;
}

export interface StartMigrationPayload {
  pluginId: string;
  merchantPluginIds: string[];
  targetPluginVersionId: string | null;
  mode: MigrationMode;
}

export const makeInitialMigrationState = (): VersionMigrationState => ({
  pluginId: null,
  merchantPluginIds: [],
  targetPluginVersionId: null,
  batchId: null,
  flaggedIds: [],
  mode: 'migrate',
});

const migrationSlice = createSlice({
  name: 'versionMigration',
  initialState: makeInitialMigrationState(),
  reducers: {
    start: (_state, { payload }: PayloadAction<StartMigrationPayload>) => ({
      ...makeInitialMigrationState(),
      pluginId: payload.pluginId,
      merchantPluginIds: [...payload.merchantPluginIds],
      targetPluginVersionId: payload.targetPluginVersionId,
      mode: payload.mode,
    }),
    setBatchId: (state, { payload }: PayloadAction<string>) => {
      state.batchId = payload;
    },
    setFlagged: (state, { payload }: PayloadAction<string[]>) => {
      state.flaggedIds = [...payload];
    },
    reset: () => makeInitialMigrationState(),
  },
});

export const versionMigrationReducer = migrationSlice.reducer;
export const migrationActions = migrationSlice.actions;
